// src/prototype/data/himss/himssAssessments.ts
// ============================================================
// HIMSS ASSESSMENT INTAKE (D6, D7) — types, validation, display helpers
//
// Source of truth for the fields: AP_DVA_Personalization_Data_Source_Plan_v0_1, tab 1
// Client Documents, rows D6 and D7 (Oct 21 enhancements, E1).
//
// CLIENT-LEVEL records, not use-case-level. Shape mirrors what Rahul's table will need:
// model, stage, validation type, date, scope, file reference, entered-by/at.
//
// AT GA THIS IS CONTEXT ONLY. Values are shown back to the client for validation. They are
// NOT a scoring input and have NO R(c) wiring. The D1/D3 consistency flag is a methodology
// rule Brian owns; until it is defined nothing here compares against self-ratings.
//
// COPY RULE: HIMSS model names are trademarks, used only as factual labels for the client's
// own results. No logo, no wording that implies a relationship.
// ============================================================

export const MATURITY_MODELS = [
  { id: 'EMRAM', name: 'EMRAM', longName: 'Electronic Medical Record Adoption Model' },
  { id: 'O-EMRAM', name: 'O-EMRAM', longName: 'Outpatient Electronic Medical Record Adoption Model' },
  { id: 'INFRAM', name: 'INFRAM', longName: 'Infrastructure Adoption Model' },
  { id: 'AMAM', name: 'AMAM', longName: 'Adoption Model for Analytics Maturity' },
] as const;

export type MaturityModelId = (typeof MATURITY_MODELS)[number]['id'];
export type ValidationType = 'himss_validated' | 'self_assessed';
export type ScopeType = 'whole_system' | 'listed_facilities';

/** D6 — one record per maturity model. held === null means not answered yet. */
export interface MaturityModelResult {
  model: MaturityModelId;
  held: boolean | null;
  stage: number | null; // integer 0–7, required when held
  validation: ValidationType | null;
  assessmentDate: string | null; // ISO yyyy-mm-dd
  scope: ScopeType;
  facilities: string; // when scope = listed_facilities
  fileName: string | null;
  enteredBy: string | null;
  enteredAt: string | null;
}

export const DHI_DIMENSIONS = [
  { id: 'governanceWorkforce', label: 'Governance and workforce' },
  { id: 'interoperability', label: 'Interoperability' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'personEnabledHealth', label: 'Person-enabled health' },
] as const;
export type DhiDimensionId = (typeof DHI_DIMENSIONS)[number]['id'];

/** D7 — Digital Health Indicator report. */
export interface DhiResult {
  completed: boolean | null;
  overall: number | null; // 0–400
  dimensions: Record<DhiDimensionId, number | null>;
  assessmentDate: string | null;
  scope: ScopeType;
  facilities: string;
  fileName: string | null;
  enteredBy: string | null;
  enteredAt: string | null;
}

export interface HimssAssessments {
  /** "We don't hold one": the client has none of these assessments. First-class answer. */
  noneHeld: boolean;
  models: Record<MaturityModelId, MaturityModelResult>;
  dhi: DhiResult;
}

export const DHI_OVERALL_MAX = 400;
/**
 * Per-dimension upper bound. NOT YET SET: the primer requires confirming the dimension
 * ranges against a sample DHI report before setting limits. Until then only "not negative"
 * is enforced.
 */
export const DHI_DIMENSION_MAX: number | null = null;

export function emptyModel(model: MaturityModelId): MaturityModelResult {
  return {
    model, held: null, stage: null, validation: null, assessmentDate: null,
    scope: 'whole_system', facilities: '', fileName: null, enteredBy: null, enteredAt: null,
  };
}

export function emptyHimss(): HimssAssessments {
  const models = Object.fromEntries(MATURITY_MODELS.map((m) => [m.id, emptyModel(m.id)])) as Record<MaturityModelId, MaturityModelResult>;
  return {
    noneHeld: false,
    models,
    dhi: {
      completed: null, overall: null,
      dimensions: { governanceWorkforce: null, interoperability: null, analytics: null, personEnabledHealth: null },
      assessmentDate: null, scope: 'whole_system', facilities: '', fileName: null, enteredBy: null, enteredAt: null,
    },
  };
}

function todayIso(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** Field-level errors for one maturity model record. Empty object = valid. */
export function validateModel(r: MaturityModelResult, now: Date = new Date()): Partial<Record<'stage' | 'assessmentDate' | 'facilities', string>> {
  const e: Partial<Record<'stage' | 'assessmentDate' | 'facilities', string>> = {};
  if (r.held !== true) return e;
  if (r.stage === null) e.stage = 'Stage is required';
  else if (!Number.isInteger(r.stage) || r.stage < 0 || r.stage > 7) e.stage = 'Stage must be a whole number from 0 to 7';
  if (r.assessmentDate && r.assessmentDate > todayIso(now)) e.assessmentDate = 'Date cannot be in the future';
  if (r.scope === 'listed_facilities' && !r.facilities.trim()) e.facilities = 'List the facilities in scope';
  return e;
}

export function validateDhi(d: DhiResult, now: Date = new Date()): Partial<Record<'overall' | 'assessmentDate' | 'facilities' | DhiDimensionId, string>> {
  const e: Partial<Record<'overall' | 'assessmentDate' | 'facilities' | DhiDimensionId, string>> = {};
  if (d.completed !== true) return e;
  if (d.overall !== null && (d.overall < 0 || d.overall > DHI_OVERALL_MAX)) e.overall = `Overall score must be from 0 to ${DHI_OVERALL_MAX}`;
  for (const dim of DHI_DIMENSIONS) {
    const v = d.dimensions[dim.id];
    if (v === null) continue;
    if (v < 0) e[dim.id] = 'Score cannot be negative';
    else if (DHI_DIMENSION_MAX !== null && v > DHI_DIMENSION_MAX) e[dim.id] = `Score must be at most ${DHI_DIMENSION_MAX}`;
  }
  if (d.assessmentDate && d.assessmentDate > todayIso(now)) e.assessmentDate = 'Date cannot be in the future';
  if (d.scope === 'listed_facilities' && !d.facilities.trim()) e.facilities = 'List the facilities in scope';
  return e;
}

/** How many of the five assessments have an answer (held yes/no, or DHI completed yes/no). */
export function answeredCount(h: HimssAssessments): number {
  if (h.noneHeld) return MATURITY_MODELS.length + 1;
  return MATURITY_MODELS.filter((m) => h.models[m.id].held !== null).length + (h.dhi.completed !== null ? 1 : 0);
}

/** How many are held with results recorded. */
export function heldCount(h: HimssAssessments): number {
  if (h.noneHeld) return 0;
  return MATURITY_MODELS.filter((m) => h.models[m.id].held === true).length + (h.dhi.completed === true ? 1 : 0);
}

function fmtDate(iso: string | null): string {
  if (!iso) return 'date not given';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function fmtScope(scope: ScopeType, facilities: string): string {
  return scope === 'whole_system' ? 'whole system' : `facilities: ${facilities.trim() || 'not listed'}`;
}

/** Plain read-back lines for client validation. */
export function readBackLines(h: HimssAssessments): string[] {
  if (h.noneHeld) return ['No HIMSS assessments held'];
  const out: string[] = [];
  for (const m of MATURITY_MODELS) {
    const r = h.models[m.id];
    if (r.held !== true) continue;
    const v = r.validation === 'himss_validated' ? 'validated by HIMSS' : r.validation === 'self_assessed' ? 'self-assessed' : 'validation not given';
    out.push(`${m.name} Stage ${r.stage ?? '?'}, ${v}, ${fmtDate(r.assessmentDate)}, ${fmtScope(r.scope, r.facilities)}`);
  }
  if (h.dhi.completed === true) {
    const dims = DHI_DIMENSIONS
      .filter((d) => h.dhi.dimensions[d.id] !== null)
      .map((d) => `${d.label} ${h.dhi.dimensions[d.id]}`)
      .join(', ');
    out.push(`Digital Health Indicator ${h.dhi.overall ?? '?'} of ${DHI_OVERALL_MAX}${dims ? ` (${dims})` : ''}, ${fmtDate(h.dhi.assessmentDate)}, ${fmtScope(h.dhi.scope, h.dhi.facilities)}`);
  }
  return out;
}
